/* Cincy preview controller (shared by the upload flow and the text editor).
   Wraps the design Worker's render engine (window.CincyEmbed).

   Everything happens on the MAIN PRODUCT PHOTO (Dawn's media gallery, which
   already shows the selected hat). A small toolbar on the photo switches
   between two modes:
   - Position patch: drag the patch around the hat, drag its corner dot to
     resize it -> custom placement {x, cy (center y), w} fed back through the
     engine.
   - Arrange design: the photo zooms in on the patch; drag anywhere to move the
     artwork inside the patch, drag a corner (or wheel / − +) to scale it,
     Center / Fit reset it.
   The art arrangement is done client-side: the raw art is wrapped in a
   positioned <svg class="cincy-art"> and handed to the engine at size 1 (art
   box = whole shape), so no Worker change is needed and drags just update that
   element's attributes.
   Pages without a gallery fall back to the in-panel On-hat / Patch tabs (the
   Patch tab carries the same art editor).
   Reports {art, pos} so the values can be stored on the order.

   customArrange (the text editor): the art always fills the patch box and the
   caller arranges its own pieces — no whole-design box or size tools; instead
   decorate({patch, svg, bb, isArt}) is called after each draw so it can add
   its own handles to the drawn patch.

   Usage:
     var ctl = CincyPreview.create({
       stage, getRaw, getSelections, onChange,  // onChange receives {art, pos}
       customArrange, artTip, decorate          // optional (see above)
     });
     ctl.render();
*/
(function () {
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  // Art wrapper units: the patch's shape bbox is mapped to W x (W * aspect).
  var W = 1000;
  var Z_MIN = 0.15, Z_MAX = 4;

  var ART_BOX =
    '<div class="cincy-artbox">' +
    '<span class="cincy-artbox__h" data-c="nw"></span><span class="cincy-artbox__h" data-c="ne"></span>' +
    '<span class="cincy-artbox__h" data-c="sw"></span><span class="cincy-artbox__h" data-c="se"></span>' +
    '</div>';

  // Pull viewBox / aspect / inner markup out of an <svg> string.
  function parseArt(raw) {
    var s = String(raw || '').replace(/<\?xml[\s\S]*?\?>/i, '').replace(/<!DOCTYPE[\s\S]*?>/i, '').trim();
    var m = s.match(/<svg\b[^>]*>/i);
    if (!m) return null;
    var tag = m[0];
    var vb = (tag.match(/viewBox\s*=\s*["']([^"']+)["']/i) || [])[1];
    if (!vb) {
      var w = (tag.match(/\bwidth\s*=\s*["']?([\d.]+)/i) || [])[1] || '100';
      var h = (tag.match(/\bheight\s*=\s*["']?([\d.]+)/i) || [])[1] || '100';
      vb = '0 0 ' + w + ' ' + h;
    }
    var n = vb.split(/[\s,]+/).map(parseFloat);
    return {
      vb: vb,
      ar: (n[2] > 0 && n[3] > 0) ? n[3] / n[2] : 1,
      inner: s.slice(m.index + tag.length, s.lastIndexOf('</svg>'))
    };
  }

  function create(opts) {
    var stage = opts.stage;
    if (!stage) return { render: function () {}, reveal: function () {} };
    var controls = stage.querySelector('.cincy-uprev__controls');
    var hatView = stage.querySelector('.cincy-uprev__view[data-view="hat"]');
    var patchView = stage.querySelector('.cincy-uprev__view[data-view="patch"]');
    // Art scale (1 = fits the patch) + center offset (fractions of the patch box).
    var custom = !!opts.customArrange;
    var art = { z: custom ? 1 : ((window.CincyEmbed && window.CincyEmbed.DEFAULT_SIZE) || 0.82), ox: 0, oy: 0 };
    var pos = null;          // {x, cy, w} custom placement on the hat
    // Arranging is optional: until the customer moves something, the order
    // says "Default" so the team knows to center + size it themselves.
    var touched = { art: false, pos: false };
    var mode = 'patch';      // on-photo mode: 'patch' | 'art'
    var parsed = null, parsedRaw = null;
    var bb = null;           // current shape bbox {x, y, w, h}
    var note = null;         // panel note shown in on-photo mode

    function persist() {
      if (opts.onChange) opts.onChange({ art: { z: art.z, ox: art.ox, oy: art.oy }, pos: pos, touched: touched });
    }

    // Art rect in wrapper units (W x W*aspect), "meet"-fitted then scaled.
    function artRect() {
      var H = W * (bb.h / bb.w);
      var dw = Math.min(W, H / parsed.ar), dh = dw * parsed.ar;
      var w = dw * art.z, h = dh * art.z;
      var cx = W / 2 + art.ox * W, cy = H / 2 + art.oy * H;
      return { x: cx - w / 2, y: cy - h / 2, w: w, h: h, H: H };
    }

    function arrangedRaw() {
      var r = artRect();
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + r.H + '">' +
        '<svg class="cincy-art" x="' + r.x + '" y="' + r.y + '" width="' + r.w + '" height="' + r.h +
        '" viewBox="' + parsed.vb + '" preserveAspectRatio="xMidYMid meet">' + parsed.inner + '</svg></svg>';
    }

    // Every place this controller has drawn the patch.
    function scopes() {
      return [patchView, hatView, layerEl].filter(Boolean);
    }

    // Cheap live update while dragging: move the art element in every rendered
    // patch instead of re-rendering, then sync outlines + readouts.
    function applyArt() {
      if (!parsed || !bb) return;
      var r = artRect();
      scopes().forEach(function (sc) {
        sc.querySelectorAll('svg.cincy-art').forEach(function (el) {
          el.setAttribute('x', r.x); el.setAttribute('y', r.y);
          el.setAttribute('width', r.w); el.setAttribute('height', r.h);
        });
        sc.querySelectorAll('.cincy-artbox').forEach(placeArtBox);
      });
      var pct = Math.round(art.z * 100) + '%';
      if (layerEl) layerEl.querySelectorAll('.cincy-onphoto__pct').forEach(function (el) { el.textContent = pct; });
      var slider = controls && controls.querySelector('.cincy-uprev__size');
      if (slider && Math.abs(parseFloat(slider.value) - art.z) > 0.005) slider.value = art.z;
    }

    // Dashed outline over the art, positioned in % of the patch <svg> beside
    // it. Shows the art's full extent even where it runs past the patch edge.
    function placeArtBox(box) {
      var svg = box.parentNode && box.parentNode.querySelector(':scope > svg');
      if (!svg || !svg.viewBox || !svg.viewBox.baseVal) return;
      var vb = svg.viewBox.baseVal;
      var r = artRect();
      var X = bb.x + (r.x / W) * bb.w, Y = bb.y + (r.y / r.H) * bb.h;
      box.style.left = ((X - vb.x) / vb.width * 100) + '%';
      box.style.top = ((Y - vb.y) / vb.height * 100) + '%';
      box.style.width = ((r.w / W) * bb.w / vb.width * 100) + '%';
      box.style.height = ((r.h / r.H) * bb.h / vb.height * 100) + '%';
    }

    function setArt(next) {
      if ('z' in next) art.z = clamp(next.z, Z_MIN, Z_MAX);
      if ('ox' in next) art.ox = clamp(next.ox, -1, 1);
      if ('oy' in next) art.oy = clamp(next.oy, -1, 1);
      touched.art = true;
      applyArt();
      persist();
    }

    // Art editing: pointerdown on `target` moves the art (a corner handle in
    // `box` scales it about its center); wheel over `target` scales it.
    // `svg` is the patch drawing the art is measured against.
    function bindArtEditor(target, svg, box, enabled) {
      if (!target || !svg || !box) return;
      var m = null, sx = 0, sy = 0, s0 = null, rect = null, vb = null, c = null, d0 = 1;
      var queued = false;

      function onMove(e) {
        if (!m) return;
        if (m === 'move') {
          art.ox = clamp(s0.ox + (e.clientX - sx) / rect.width * (vb.width / bb.w), -1, 1);
          art.oy = clamp(s0.oy + (e.clientY - sy) / rect.height * (vb.height / bb.h), -1, 1);
        } else {
          var d = Math.hypot(e.clientX - c.x, e.clientY - c.y) || 1;
          art.z = clamp(s0.z * d / d0, Z_MIN, Z_MAX);
        }
        if (!queued) {
          queued = true;
          requestAnimationFrame(function () { queued = false; applyArt(); });
        }
        e.preventDefault();
      }
      function onUp() {
        if (!m) return;
        m = null;
        box.classList.remove('is-dragging');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        if (art.z !== s0.z || art.ox !== s0.ox || art.oy !== s0.oy) touched.art = true;
        applyArt();
        persist();
      }
      target.addEventListener('pointerdown', function (e) {
        if (enabled && !enabled()) return;
        if (e.target.closest && e.target.closest('.cincy-onphoto__bar')) return;
        m = e.target.classList.contains('cincy-artbox__h') ? 'scale' : 'move';
        rect = svg.getBoundingClientRect();
        vb = svg.viewBox.baseVal;
        sx = e.clientX; sy = e.clientY;
        s0 = { z: art.z, ox: art.ox, oy: art.oy };
        var br = box.getBoundingClientRect();
        c = { x: br.left + br.width / 2, y: br.top + br.height / 2 };
        d0 = Math.hypot(e.clientX - c.x, e.clientY - c.y) || 1;
        box.classList.add('is-dragging');
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        e.preventDefault();
        e.stopPropagation();
      });
      target.addEventListener('wheel', function (e) {
        if (enabled && !enabled()) return;
        e.preventDefault();
        setArt({ z: art.z * Math.exp(-e.deltaY * 0.0015) });
      }, { passive: false });
    }

    // Patch positioning: drag to move, corner dot to resize. `hat` is the box
    // the placement fractions are relative to.
    function bindPatchMove(hat, patch, enabled) {
      if (!hat || !patch) return;
      patch.classList.add('is-draggable');

      var handle = document.createElement('span');
      handle.className = 'cincy-uprev__resize';
      patch.appendChild(handle);

      var m = null, sx = 0, sy = 0, sp = null, rect = null;

      function onMove(e) {
        if (!m) return;
        var dx = (e.clientX - sx) / rect.width;
        var dy = (e.clientY - sy) / rect.height;
        if (m === 'move') {
          pos.x = clamp(sp.x + dx, 0.08, 0.92);
          pos.cy = clamp(sp.cy + dy, 0.08, 0.92);
          patch.style.left = (pos.x * 100) + '%';
          patch.style.top = (pos.cy * 100) + '%';
        } else {
          pos.w = clamp(sp.w + dx * 2, 0.08, 0.95);
          patch.style.width = (pos.w * 100) + '%';
        }
        e.preventDefault();
      }
      function onUp() {
        if (!m) return;
        m = null;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        if (pos.x !== sp.x || pos.cy !== sp.cy || pos.w !== sp.w) touched.pos = true;
        persist();
      }
      patch.addEventListener('pointerdown', function (e) {
        if (enabled && !enabled()) return;
        m = (e.target === handle) ? 'resize' : 'move';
        rect = hat.getBoundingClientRect();
        sx = e.clientX; sy = e.clientY;
        sp = { x: pos.x, cy: pos.cy, w: pos.w };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        e.preventDefault();
        e.stopPropagation();
      });
      // Keep clicks on the patch from opening the product photo lightbox.
      patch.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
    }

    // ---- On-photo toolbar + modes ------------------------------------------
    function buildBar(layer) {
      var bar = document.createElement('div');
      bar.className = 'cincy-onphoto__bar';
      bar.innerHTML =
        '<div class="cincy-onphoto__seg" role="tablist">' +
        '<button type="button" data-mode="patch">Position patch</button>' +
        '<button type="button" data-mode="art">Arrange design</button>' +
        '</div>' +
        '<div class="cincy-onphoto__tools">' +
        '<button type="button" data-act="minus" aria-label="Smaller">−</button>' +
        '<span class="cincy-onphoto__pct"></span>' +
        '<button type="button" data-act="plus" aria-label="Bigger">+</button>' +
        '<button type="button" data-act="center">Center</button>' +
        '<button type="button" data-act="fit">Fit</button>' +
        '</div>';
      bar.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        e.preventDefault();
        e.stopPropagation();
        if (!b) return;
        if (b.dataset.mode) { setMode(b.dataset.mode); return; }
        var a = b.dataset.act;
        if (a === 'minus') setArt({ z: art.z / 1.1 });
        else if (a === 'plus') setArt({ z: art.z * 1.1 });
        else if (a === 'center') setArt({ ox: 0, oy: 0 });
        else if (a === 'fit') setArt({ z: 1, ox: 0, oy: 0 });
      });
      bar.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
      layer.appendChild(bar);
      var tip = document.createElement('p');
      tip.className = 'cincy-onphoto__tip';
      layer.appendChild(tip);
    }

    function setMode(next) {
      mode = next;
      if (!layerEl) return;
      var isArt = mode === 'art';
      layerEl.classList.toggle('is-art', isArt);
      layerEl.querySelectorAll('.cincy-onphoto__seg button').forEach(function (b) {
        var on = b.dataset.mode === mode;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      var tip = layerEl.querySelector('.cincy-onphoto__tip');
      if (tip) {
        tip.textContent = isArt
          ? (opts.artTip || 'Drag to move your design · drag a corner to resize')
          : 'Drag the patch to place it · corner dot resizes';
      }
      // Arrange mode zooms the photo so the patch fills ~60% of it, centered
    // (a touch low, to clear the toolbar).
      zoomState = (isArt && pos) ? zoomFor(pos) : null;
      syncZoom();
      applyArt();
    }

    function zoomFor(p) {
      var k = clamp(0.6 / p.w, 1, 3.2);
      return {
        k: k,
        o: (p.x * 100) + '% ' + (p.cy * 100) + '%',
        t: 'translate(' + ((0.5 - p.x) * 100) + '%,' + ((0.55 - p.cy) * 100) + '%) scale(' + k + ')'
      };
    }

    // Re-composite using the current art arrangement + custom placement.
    function draw() {
      var E = window.CincyEmbed;
      if (!E || !E.ready) return false;
      var raw = opts.getRaw ? opts.getRaw() : null;
      if (!raw) return false;
      if (raw !== parsedRaw) { parsed = parseArt(raw); parsedRaw = raw; }
      if (!parsed) return false;
      var ctx = E.buildContext(opts.getSelections ? opts.getSelections() : {});
      bb = ctx.shape.bbox;
      // Seed the custom placement from the profile's default the first time.
      if (!pos) {
        var d = ctx.profile.placements[ctx.profile.defaultPlacement] || { x: 0.5, w: 0.3, cy: 0.46 };
        pos = { x: d.x, cy: (typeof d.cy === 'number' ? d.cy : 0.46), w: d.w };
      }
      ctx.profile.placements.__custom = { label: 'Custom', x: pos.x, w: pos.w, cy: pos.cy };
      var views = E.makeViews(arrangedRaw(), 1, null, '__custom');

      if (photoMedia()) {
        // On the main product photo: it already shows the selected hat, so
        // only the patch layer (+ toolbar) is drawn on top of it.
        if (hatView) hatView.innerHTML = '';
        if (patchView) patchView.innerHTML = '';
        var tmp = document.createElement('div');
        tmp.innerHTML = views.hat;
        var patch = tmp.querySelector('.patchpos');
        if (!custom) patch.insertAdjacentHTML('beforeend', ART_BOX);
        var layer = overlay();
        layer.innerHTML = '';
        layer.classList.toggle('is-custom', custom);
        var zoomBox = document.createElement('div');
        zoomBox.className = 'cincy-onphoto__stage';
        zoomBox.appendChild(patch);
        layer.appendChild(zoomBox);
        buildBar(layer);
        mountOverlay();
        bindPatchMove(zoomBox, patch, function () { return mode === 'patch'; });
        if (!custom) {
          bindArtEditor(zoomBox, patch.querySelector(':scope > svg'), patch.querySelector('.cincy-artbox'),
            function () { return mode === 'art'; });
        }
        setOnPhoto(true);
        setMode(mode);
        if (custom && opts.decorate) {
          opts.decorate({ patch: patch, svg: patch.querySelector(':scope > svg'), bb: bb, isArt: function () { return mode === 'art'; } });
        }
      } else {
        // No product gallery on the page — fall back to the in-panel tabs.
        if (hatView) hatView.innerHTML = views.hat;
        var img = hatView && hatView.querySelector('img');
        if (img) {
          img.addEventListener('error', function () {
            var def = E.origin + '/mock/hats/' + E.registry.defaultHat + '.png';
            if (img.src.indexOf(E.registry.defaultHat) === -1) img.src = def;
          });
        }
        bindPatchMove(hatView && hatView.querySelector('.hat'), hatView && hatView.querySelector('.patchpos'));
        if (patchView) {
          patchView.innerHTML = '<div class="cincy-arted">' + views.patch + (custom ? '' : ART_BOX) + '</div>';
          var ed = patchView.querySelector('.cincy-arted');
          if (custom) {
            if (opts.decorate) opts.decorate({ patch: ed, svg: ed.querySelector(':scope > svg'), bb: bb, isArt: function () { return true; } });
          } else {
            bindArtEditor(ed, ed.querySelector(':scope > svg'), ed.querySelector('.cincy-artbox'));
          }
        }
        if (!custom) buildPanelControls();
        setOnPhoto(false);
        applyArt();
      }
      stage.hidden = false;
      persist();
      return true;
    }

    // Fallback-only size slider + buttons (on-photo mode uses the toolbar).
    function buildPanelControls() {
      if (!controls || controls.firstChild) return;
      controls.innerHTML =
        '<label class="cincy-uprev__ctl"><span class="cincy-uprev__ctl-lab">Design size</span>' +
        '<input type="range" class="cincy-uprev__size" min="' + Z_MIN + '" max="' + Z_MAX + '" step="0.01" value="' + art.z + '"></label>' +
        '<span class="cincy-uprev__btns">' +
        '<button type="button" class="cincy-uprev__mini" data-art="center">Center</button>' +
        '<button type="button" class="cincy-uprev__mini" data-art="fit">Fit to patch</button>' +
        '</span>' +
        '<span class="cincy-uprev__hint">Patch tab: drag your design to move it, drag a corner to resize. On hat tab: drag the patch to place it.</span>';
      controls.querySelector('.cincy-uprev__size').addEventListener('input', function () {
        setArt({ z: parseFloat(this.value) });
      });
      controls.querySelectorAll('[data-art]').forEach(function (b) {
        b.addEventListener('click', function () {
          setArt(b.getAttribute('data-art') === 'fit' ? { z: 1, ox: 0, oy: 0 } : { ox: 0, oy: 0 });
        });
      });
    }

    // Panel layout: on-photo mode hides the in-panel previews/controls and
    // shows a short note pointing at the photo.
    function setOnPhoto(on) {
      stage.classList.toggle('is-onphoto', on);
      if (controls) controls.hidden = on;
      var tabs = stage.querySelector('.cincy-uprev__tabs');
      if (tabs) tabs.hidden = on;
      if (hatView) hatView.hidden = on || !stage.querySelector('.cincy-uprev__tab[data-view="hat"].is-on');
      if (patchView) patchView.hidden = on || !stage.querySelector('.cincy-uprev__tab[data-view="patch"].is-on');
      if (on && !note) {
        note = document.createElement('p');
        note.className = 'cincy-uprev__photonote';
        // Phones only (CSS): the photo sits above the form there.
        note.innerHTML = '<button type="button" class="cincy-uprev__mini">↑ Arrange it on the hat photo</button>';
        note.querySelector('button').addEventListener('click', function () {
          var m = photoMedia();
          if (m) m.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setMode('art');
        });
        stage.insertBefore(note, stage.firstChild);
      }
      if (note) note.hidden = !on;
    }

    // True when this controller's method panel is the one showing.
    function isActive() {
      var panel = stage.closest('.cincy-panel');
      return !panel || !panel.hidden;
    }

    var api = {
      render: function () { return isActive() ? draw() : false; },
      getState: function () { return { art: art, pos: pos }; },
      setMode: function (m) { setMode(m); },
      // Scroll the main photo into view if it's off screen (mobile: the
      // gallery sits above the form).
      reveal: function () {
        var m = photoMedia();
        if (!m) return;
        var r = m.getBoundingClientRect();
        if (r.bottom < 80 || r.top > window.innerHeight - 80) {
          m.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    };
    controllers.push(api);

    // Tabs (fallback mode)
    stage.querySelectorAll('.cincy-uprev__tab').forEach(function (t) {
      t.addEventListener('click', function () {
        var which = t.getAttribute('data-view');
        stage.querySelectorAll('.cincy-uprev__tab').forEach(function (x) { x.classList.toggle('is-on', x === t); });
        stage.querySelectorAll('.cincy-uprev__view').forEach(function (v) {
          v.hidden = v.getAttribute('data-view') !== which;
        });
        if (which === 'patch') applyArt();
      });
    });

    return api;
  }

  // ---- Patch layer on the main product photo -------------------------------
  // One shared layer; whichever method panel rendered last owns it.
  var controllers = [];
  var layerEl = null;
  var observing = false;
  var zoomState = null;      // {o: transform-origin, t: transform} while arranging
  var zoomedImg = null;

  // The active (or first) media box in Dawn's product gallery.
  function photoMedia() {
    var g = document.querySelector('media-gallery');
    if (!g) return null;
    var item = g.querySelector('.product__media-item.is-active') || g.querySelector('.product__media-item');
    var media = item && item.querySelector('.product__media');
    return media && photoImg(media) ? media : null;
  }

  function photoImg(media) {
    for (var i = 0; i < media.children.length; i++) {
      if (media.children[i].tagName === 'IMG') return media.children[i];
    }
    return null;
  }

  function overlay() {
    if (!layerEl) {
      layerEl = document.createElement('div');
      layerEl.className = 'cincy-onphoto';
    }
    return layerEl;
  }

  // Apply (or clear) the arrange-mode zoom on the photo + patch layer together.
  function syncZoom() {
    var m = layerEl && layerEl.parentNode;
    var img = m ? photoImg(m) : null;
    if (zoomedImg && zoomedImg !== img) {
      zoomedImg.style.transform = '';
      zoomedImg.style.transformOrigin = '';
    }
    zoomedImg = img;
    var st = layerEl && layerEl.querySelector('.cincy-onphoto__stage');
    [img, st].forEach(function (el) {
      if (!el) return;
      el.style.transition = 'transform 0.25s ease';
      el.style.transformOrigin = zoomState ? zoomState.o : '';
      el.style.transform = zoomState ? zoomState.t : '';
    });
    if (st) st.style.setProperty('--k', zoomState ? zoomState.k : 1);
  }

  // (Re)attach the layer to the active photo. Dawn prepends/removes gallery
  // items and moves `is-active` when the hat color changes, so watch for that.
  function mountOverlay() {
    var m = photoMedia();
    if (!m || !layerEl || !layerEl.firstChild) return;
    if (layerEl.parentNode !== m) m.appendChild(layerEl);
    syncZoom();
    if (!observing) {
      var g = document.querySelector('media-gallery');
      if (g && window.MutationObserver) {
        var queued = false;
        new MutationObserver(function () {
          if (queued) return;
          queued = true;
          requestAnimationFrame(function () { queued = false; mountOverlay(); });
        }).observe(g, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        observing = true;
      }
    }
  }

  function clearOverlay() {
    zoomState = null;
    syncZoom();
    if (layerEl) {
      layerEl.innerHTML = '';
      if (layerEl.parentNode) layerEl.parentNode.removeChild(layerEl);
    }
  }

  // Switching design method: drop the old patch, let the new panel redraw.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.name !== 'properties[Design Method]') return;
    clearOverlay();
    setTimeout(function () {
      controllers.forEach(function (c) { c.render(); });
    }, 0);
  });

  // Resolve the "On hat" background: the selected variant's image (from the
  // data-variant-images map on <cincy-customizer>), falling back to the default.
  function hatImage(customizer) {
    if (!customizer) return '';
    var map = {};
    try { map = JSON.parse(customizer.getAttribute('data-variant-images') || '{}'); } catch (e) {}
    var form = customizer.closest('form');
    var idEl = form && form.querySelector('[name="id"]');
    var id = idEl && idEl.value;
    if (id && map[id]) return map[id];
    return customizer.getAttribute('data-default-hat') || '';
  }

  // Untouched values — must match the defaults in cincy-customizer-fields.liquid.
  var DEFAULT_DESIGN = 'Default (we size and center it)';
  var DEFAULT_PLACEMENT = 'Default (we place it)';

  // Human-readable line-item values for the team: design scale/offset inside
  // the patch, and the patch's spot on the hat.
  function describe(state) {
    var out = { design: DEFAULT_DESIGN, placement: DEFAULT_PLACEMENT };
    var t = (state && state.touched) || { art: true, pos: true };
    var a = state && state.art;
    if (a && t.art) {
      var h = Math.round(a.ox * 100), v = Math.round(a.oy * 100);
      out.design = Math.round(a.z * 100) + '% of patch' +
        (h || v ? ' · shifted ' + (h ? Math.abs(h) + '% ' + (h > 0 ? 'right' : 'left') : '') +
          (h && v ? ', ' : '') + (v ? Math.abs(v) + '% ' + (v > 0 ? 'down' : 'up') : '') : ' · centered');
    }
    var p = state && state.pos;
    if (p && t.pos) {
      out.placement = 'pos ' + Math.round(p.x * 100) + '%,' + Math.round(p.cy * 100) +
        '% · width ' + Math.round(p.w * 100) + '%';
    }
    return out;
  }

  window.CincyPreview = { create: create, hatImage: hatImage, describe: describe, clear: clearOverlay, parseArt: parseArt };
})();
